import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { alpha, keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { CiImageOn } from 'react-icons/ci';
import { MdChevronRight } from 'react-icons/md';
import { TAppConfig } from 'src/config';

import { PaymentRequiredBadge, PaymentRequiredBadgeBg, UnlockBadge } from './card-lock-badges';

const FallbackThumbnail = () => (
  <Box
    sx={{
      width: '100%',
      aspectRatio: '370 / 264',
      borderRadius: 2,
      backgroundColor: '#f6ebe9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CiImageOn size={64} color="#d8aaa2" />
  </Box>
);

export const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

type TProgramCardProps = {
  app: TAppConfig;
  loading: string | null;
  tier?: number;
  isActive: boolean;
  hasPaymentIssue?: boolean;
  isMobile?: boolean;
  cardName?: string;
  onClick: (app: TAppConfig) => void;
};

export const ProgramCard = (props: TProgramCardProps) => {
  const { app, tier, isActive, loading, onClick, isMobile, cardName, hasPaymentIssue = false } = props;
  const displayName = cardName ?? (isActive ? app.name.subscribed : app.name.unsubscribed);

  const thumbnail = app.thumbnails?.[`TIER_${tier}` as keyof typeof app.thumbnails] || app.thumbnails.default;

  const isSubscribed = isActive;

  const [imageFailed, setImageFailed] = useState(false);
  const showFallback = !thumbnail || imageFailed;

  if (isMobile) {
    return (
      <Card
        sx={{
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0px 0px 10px 0px #cacaca',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <CardActionArea onClick={() => onClick(app)} disabled={loading !== null}>
          <Box sx={{ position: 'relative', borderRadius: 3 }}>
            {!showFallback ? (
              <Box
                component="img"
                src={thumbnail}
                onError={() => setImageFailed(true)}
                sx={{ width: '100%', aspectRatio: '370 / 264', display: 'block', objectFit: 'cover' }}
              />
            ) : (
              <FallbackThumbnail />
            )}
            {hasPaymentIssue && <PaymentRequiredBadgeBg />}
          </Box>

          {/* Gradient overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 50%)',
            }}
          />

          {/* Title bottom-left */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
            }}
          >
            <Typography
              sx={{
                color: '#fff',
                fontSize: '1rem',
                lineHeight: 1.2,
              }}
              dangerouslySetInnerHTML={{
                __html: displayName,
              }}
            />

            {/* Arrow button bottom-right */}
            <IconButton
              sx={{
                background: theme => `linear-gradient(90deg, ${theme.palette.primary.light} 0%, #FF7262 100%)`,
                borderRadius: 2,
                width: 30,
                height: 30,
                '&:hover': {
                  bgcolor: 'error.dark',
                },
              }}
            >
              <MdChevronRight color="#fff" size={24} />
            </IconButton>
          </Box>

          {loading === app.id && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: alpha('#ffffff', 0.8),
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99,
              }}
            >
              <CircularProgress size={36} thickness={2} />
            </Box>
          )}

          {!isSubscribed && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'inherit',
                ...(hasPaymentIssue ? {} : { bgcolor: alpha('#9A8787', 0.75) }),
              }}
            >
              {hasPaymentIssue ? <PaymentRequiredBadge /> : <UnlockBadge />}
            </Box>
          )}
        </CardActionArea>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        position: 'relative',
        p: 0.4,
        backgroundColor: '#FFF7F5',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0px 0px 10px 0px #cacaca',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme =>
            `0 10px 20px ${alpha(theme.palette.grey[400], 0.25)}, 0 10px 20px ${alpha(theme.palette.grey[400], 0.15)}`,
          '&::before': {
            animation: `${shimmer} 2s linear infinite`,
          },
        },
      }}
    >
      <CardActionArea onClick={() => onClick(app)} disabled={loading !== null}>
        <Box sx={{ position: 'relative', borderRadius: 3 }}>
          {!showFallback ? (
            <Box
              component="img"
              src={thumbnail}
              onError={() => setImageFailed(true)}
              sx={{
                width: '100%',
                aspectRatio: '370 / 264',
                objectFit: 'cover',
                borderRadius: 3,
              }}
            />
          ) : (
            <FallbackThumbnail />
          )}
          {hasPaymentIssue && <PaymentRequiredBadgeBg />}
        </Box>

        <Box>
          {loading === app.id && (
            <Box
              className="launch-button"
              sx={{
                position: 'absolute',
                top: 0,
                background: alpha('#ffffff', 0.8),
                width: '100%',
                height: '100%',
                zIndex: 99,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            >
              <CircularProgress size={36} thickness={2} />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '0.9rem',
              color: theme => theme.palette.common.black,
              backgroundColor: '#FFF7F5,',
            }}
            dangerouslySetInnerHTML={{
              __html: displayName,
            }}
          />

          {isSubscribed && (
            <IconButton
              sx={{
                background: theme => `linear-gradient(90deg, ${theme.palette.primary.light} 0%, #FF7262 100%)`,
                borderRadius: 2,
                width: 30,
                height: 30,
                '&:hover': {
                  bgcolor: 'error.dark',
                },
              }}
            >
              <MdChevronRight color="#fff" size={24} />
            </IconButton>
          )}
        </Box>

        {!isSubscribed && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'inherit',
              ...(hasPaymentIssue ? {} : { bgcolor: alpha('#9A8787', 0.55) }),
            }}
          >
            {hasPaymentIssue ? <PaymentRequiredBadge /> : <UnlockBadge />}
          </Box>
        )}
      </CardActionArea>
    </Card>
  );
};
